import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import type { AttackSurfaceSelection } from '@/components/AttackSurfaceSelector';
import { AttackSurfaceSelector } from '@/components/AttackSurfaceSelector';
import { StyledDatePicker } from '@/components/form/StyledDatePicker';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import { useGetMonitoringCounts } from '@/hooks/useGetMonitoringStats';
import { useMonitoringCountMode } from '@/hooks/useMonitoringCountMode';
import { useMSRPCalculator } from '@/hooks/useMSRPCalculator';
import {
  useGetSubscription,
  useUpdateSubscription,
} from '@/hooks/useSubscription';
import { MonitoringCountModeToggle } from '@/sections/monitoring/MonitoringCountModeToggle';
import {
  ScanningFrequencySelector,
  SubscriptionTierSelector,
} from '@/sections/settings/subscription/components';
import {
  AICreditsSection,
  MSRPPriceSection,
  ReviewSection,
  UsagePriceSection,
} from '@/sections/settings/subscription/configure/components';
import {
  AI_CREDIT_PACKS,
  type AICreditPackId,
  ScanFrequency,
} from '@/sections/settings/subscription/constant';
import { useAuth } from '@/state/auth';
import { SubscriptionConfiguration } from '@/types';
import { cn } from '@/utils/classname';
import { calculateAssetTotal } from '@/utils/monitoring';

interface ConfigureSubscriptionProps {
  open: boolean;
  onClose: () => void;
  customerEmail?: string;
}

export function ConfigureSubscription({
  open,
  onClose,
  customerEmail,
}: ConfigureSubscriptionProps) {
  // State management with default values
  const { isPraetorianUser } = useAuth();
  const { mode } = useMonitoringCountMode(customerEmail);

  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  const [localSubscription, setLocalSubscription] =
    useState<SubscriptionConfiguration>({
      estimatedCost: 0,
      attackSurface: {
        external: false,
        applications: false,
        cloud: false,
        code: false,
        internal: false,
        users: false,
        device: false,
        llm: false,
      },
      numberOfAssets: 0,
      numberOfApplications: 0,
      numberOfLLMs: 0,
      numberOfAEVExercises: 0,
      aiCreditPack: 'none',
      scanSchedule: 'weekly',
      selectedTier: 'professional',
      startDate: '',
      endDate: '',
    });

  const { data: subscriptionValue, status: subscriptionStatus } =
    useGetSubscription({ email: customerEmail });

  const { mutateAsync: updateSubscription, status: updateSubscriptionStatus } =
    useUpdateSubscription({ email: customerEmail });

  // Display counts - respects count mode toggle
  const { data: displayMonitoringCounts } = useGetMonitoringCounts({
    email: customerEmail,
    countType:
      mode === 'last-scan' ? 'active_monitoring_count' : 'monitoring_count',
  });

  // Pricing counts - always use 30-day rolling for consistent pricing
  const { data: pricingMonitoringCounts } = useGetMonitoringCounts({
    email: customerEmail,
    countType: 'monitoring_count',
  });

  // Display total - shown in UsagePriceSection "Current Asset Usage"
  const displayTotalAssets = !displayMonitoringCounts
    ? 0
    : calculateAssetTotal(
        displayMonitoringCounts,
        localSubscription.attackSurface
      );

  // Pricing total - always 30-day rolling, used for price calculations
  const pricingTotalAssets = !pricingMonitoringCounts
    ? 0
    : calculateAssetTotal(
        pricingMonitoringCounts,
        localSubscription.attackSurface
      );

  // React 19: Simple property access doesn't need useMemo
  const actualTotalApplications =
    displayMonitoringCounts?.applications?.webseed || 0;

  // Total code repos from monitoring data
  const actualTotalCodeRepos = !displayMonitoringCounts?.code
    ? 0
    : Object.values(displayMonitoringCounts.code).reduce(
        (acc, curr) => acc + curr,
        0
      );

  // Tier-based pricing overrides
  const isFreemium = localSubscription.selectedTier === 'freemium';
  const isFoundational = localSubscription.selectedTier === 'foundational';
  // Foundational and Freemium tiers do not include web applications
  const tierIncludesWebApps = !isFreemium && !isFoundational;

  // Calculate Usage Price based on actual current usage (always 30-day rolling for consistent pricing)
  const { floor: usageFloor, msrp: usagePrice } = useMSRPCalculator({
    numberOfApplications: tierIncludesWebApps ? actualTotalApplications : 0,
    scanSchedule: localSubscription.scanSchedule,
    numberOfAssets: pricingTotalAssets,
    selectedTier: localSubscription.selectedTier,
    numberOfAEVExercises: 0,
  });

  // Calculate MSRP based on coverage limits
  const { floor, msrp, breakdown, floorBreakdown } = useMSRPCalculator({
    numberOfApplications: tierIncludesWebApps
      ? localSubscription.numberOfApplications
      : 0,
    scanSchedule: localSubscription.scanSchedule,
    numberOfAssets: localSubscription.numberOfAssets,
    selectedTier: localSubscription.selectedTier,
    numberOfAEVExercises: localSubscription.numberOfAEVExercises,
  });
  // AI credit pack annual cost
  const aiCreditPackData = AI_CREDIT_PACKS.find(
    p => p.id === (localSubscription.aiCreditPack ?? 'none')
  );
  const aiCreditAnnualCost = aiCreditPackData?.annualPrice ?? 0;

  const subscriptionMsrp = isFreemium ? 0 : msrp;
  const totalMsrp = isFreemium ? 0 : msrp + aiCreditAnnualCost;
  const subscriptionFloor = isFreemium ? 0 : floor;
  const subscriptionBreakdown = isFreemium
    ? {
        fqdnCost: 0,
        assetCost: 0,
        aevCost: 0,
      }
    : breakdown;
  const subscriptionFloorBreakdown = isFreemium
    ? {
        fqdnCost: 0,
        assetCost: 0,
        aevCost: 0,
      }
    : floorBreakdown;

  const handleSubscriptionChange = useCallback(
    (value: Partial<SubscriptionConfiguration>) => {
      setLocalSubscription(prev => ({ ...prev, ...value }));
    },
    []
  );

  const handleAttackSurfaceChange = useCallback(
    (newAttackSurface: AttackSurfaceSelection) => {
      setLocalSubscription(prev => ({
        ...prev,
        // Convert AttackSurfaceSelection to Record<string, boolean> safely
        attackSurface: { ...newAttackSurface } as Record<string, boolean>,
      }));
    },
    []
  );

  const handleAttackSurfaceSelectionChange = useCallback(
    (key: keyof AttackSurfaceSelection, selected: boolean) => {
      if (key === 'applications') {
        // When Web Applications is disabled, reset the number of applications
        // When re-enabled, restore to actual assessment count
        handleSubscriptionChange({
          numberOfApplications: selected ? actualTotalApplications : 0,
        });
      } else if (key === 'llm' && !selected) {
        // When LLM is disabled, reset numberOfLLMs to 0.
        // sanitizedSubscription already does this at save time, but clearing
        // here keeps local state consistent so the summary reflects reality.
        handleSubscriptionChange({ numberOfLLMs: 0 });
      }
    },
    [handleSubscriptionChange, actualTotalApplications]
  );

  // Memoized save handler
  const handleSave = useCallback(async () => {
    // Validate Web FQDNs when web attack surface is enabled
    if (
      localSubscription.attackSurface.applications &&
      localSubscription.numberOfApplications === 0
    ) {
      toast.error(
        '# of Web FQDNs cannot be 0 when Web attack surface is enabled'
      );
      return;
    }
    // Sanitize tier-irrelevant fields before saving to prevent invalid data
    const isProfessionalOrUltimate =
      localSubscription.selectedTier === 'professional' ||
      localSubscription.selectedTier === 'ultimate';
    const isUltimateTier = localSubscription.selectedTier === 'ultimate';
    const sanitizedSubscription = {
      ...localSubscription,
      numberOfApplications: isProfessionalOrUltimate
        ? localSubscription.numberOfApplications
        : 0,
      numberOfAEVExercises: isUltimateTier
        ? localSubscription.numberOfAEVExercises
        : 0,
      numberOfLLMs: localSubscription.attackSurface.llm
        ? (localSubscription.numberOfLLMs ?? 0)
        : 0,
      aiCreditPack: localSubscription.aiCreditPack ?? 'none',
      estimatedCost:
        localSubscription.selectedTier === 'freemium'
          ? 0
          : localSubscription.estimatedCost,
    };

    // Include MSRP in saved configuration if Praetorian user
    const configToSave = isPraetorianUser
      ? {
          ...sanitizedSubscription,
          msrp: subscriptionMsrp,
          aiCreditAnnualCost:
            AI_CREDIT_PACKS.find(
              p => p.id === (sanitizedSubscription.aiCreditPack ?? 'none')
            )?.annualPrice ?? 0,
          msrpBreakdown: subscriptionBreakdown,
          floorPrice: subscriptionFloor,
          floorBreakdown: subscriptionFloorBreakdown,
        }
      : sanitizedSubscription;
    await updateSubscription(configToSave);
    onClose();
  }, [
    localSubscription,
    updateSubscription,
    onClose,
    isPraetorianUser,
    subscriptionMsrp,
    subscriptionBreakdown,
    subscriptionFloor,
    subscriptionFloorBreakdown,
  ]);

  // Sync local subscription state when server data changes
  // Using subscriptionStatus as a stable trigger - when status changes from loading to success,
  // we know we have fresh data. This avoids JSON.stringify which breaks memoization.
  useEffect(() => {
    if (subscriptionValue && subscriptionStatus === 'success') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Syncing server data to local state on fetch success; useEffect is the correct pattern for external data sync
      setLocalSubscription(_prev => ({
        ...subscriptionValue,
        attackSurface: {
          external: subscriptionValue.attackSurface?.external ?? false,
          applications: subscriptionValue.attackSurface?.applications ?? false,
          cloud: subscriptionValue.attackSurface?.cloud ?? false,
          code: subscriptionValue.attackSurface?.code ?? false,
          internal: subscriptionValue.attackSurface?.internal ?? false,
          users: subscriptionValue.attackSurface?.users ?? false,
          device: subscriptionValue.attackSurface?.device ?? false,
          llm: subscriptionValue.attackSurface?.llm ?? false,
        },
        startDate: subscriptionValue.startDate || '',
        endDate: subscriptionValue.endDate || '',
        numberOfLLMs: subscriptionValue.numberOfLLMs ?? 0,
        numberOfAEVExercises: subscriptionValue.numberOfAEVExercises ?? 0,
        aiCreditPack: subscriptionValue.aiCreditPack ?? 'none',
      }));
    }
  }, [subscriptionStatus, subscriptionValue]);

  // Reset step when modal opens
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Resetting wizard step on modal open; useEffect is the correct pattern for responding to prop changes
      setCurrentStep(1);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Configure Subscription"
      size="7xl"
      baseClassName="max-h-[90vh]"
      disableAutoFocus={true}
      hideFooter
      data-testid="configure-subscription-modal"
      aria-labelledby="configure-subscription-title"
    >
      <Loader
        type="spinner"
        isLoading={subscriptionStatus === 'pending' && !subscriptionValue}
      >
        {/* Step indicator */}
        <div className="mb-4 flex items-center gap-2">
          {[
            { step: 1, label: 'Subscription' },
            { step: 2, label: 'AI Pack' },
            { step: 3, label: 'Review' },
          ].map(({ step, label }) => (
            <div key={step} className="flex items-center gap-2">
              {step > 1 && (
                <div
                  className={cn(
                    'h-px w-8',
                    currentStep >= step
                      ? 'bg-chariot-brand-primary'
                      : 'bg-chariot-stroke'
                  )}
                />
              )}
              <div className="flex items-center gap-1.5">
                <div
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold',
                    currentStep === step
                      ? 'bg-chariot-brand-primary text-white'
                      : currentStep > step
                        ? 'bg-chariot-brand-primary/20 text-chariot-brand-primary'
                        : 'bg-chariot-stroke text-chariot-text-secondary'
                  )}
                >
                  {currentStep > step ? '✓' : step}
                </div>
                <span
                  className={cn(
                    'text-sm font-medium',
                    currentStep === step
                      ? 'text-chariot-text-primary'
                      : 'text-chariot-text-secondary'
                  )}
                >
                  {label}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="relative h-[640px]">
          {/* Step 1: Subscription */}
          {currentStep === 1 && (
            <div
              className="absolute inset-0 space-y-3 overflow-y-auto"
              role="form"
              aria-label="Subscription configuration form"
            >
              {/* Count Mode Toggle - controls asset counting and pricing */}
              <div className="flex justify-end">
                <MonitoringCountModeToggle
                  customerEmail={customerEmail ?? ''}
                />
              </div>

              {/* Subscription Term + Attack Surface Coverage Row */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[auto_1fr]">
                <section
                  className="space-y-2"
                  aria-labelledby="term-section-title"
                >
                  <h3
                    id="term-section-title"
                    className="text-chariot-text-primary text-lg font-semibold"
                  >
                    Subscription Term
                  </h3>
                  <div className="flex items-center gap-3">
                    <label className="text-chariot-text-primary shrink-0 text-xs">
                      Start
                    </label>
                    <StyledDatePicker
                      label=""
                      name="start-date"
                      value={localSubscription.startDate || ''}
                      onChange={value =>
                        handleSubscriptionChange({ startDate: value })
                      }
                      max={localSubscription.endDate}
                      className="max-w-[160px]"
                    />
                    <label className="text-chariot-text-primary shrink-0 text-xs">
                      End
                    </label>
                    <StyledDatePicker
                      label=""
                      name="end-date"
                      value={localSubscription.endDate || ''}
                      onChange={value =>
                        handleSubscriptionChange({ endDate: value })
                      }
                      min={localSubscription.startDate}
                      className="max-w-[160px]"
                    />
                  </div>
                </section>

                <section className="space-y-4">
                  <h3 className="text-chariot-text-primary text-lg font-semibold">
                    Attack Surface Coverage
                  </h3>
                  <AttackSurfaceSelector
                    variant="compact"
                    value={{
                      external:
                        localSubscription.attackSurface.external || false,
                      applications:
                        localSubscription.attackSurface.applications || false,
                      internal:
                        localSubscription.attackSurface.internal || false,
                      cloud: localSubscription.attackSurface.cloud || false,
                      code: localSubscription.attackSurface.code || false,
                      users: localSubscription.attackSurface.users || false,
                      device: localSubscription.attackSurface.device || false,
                      llm: localSubscription.attackSurface.llm || false,
                    }}
                    onChange={handleAttackSurfaceChange}
                    onSelectionChange={handleAttackSurfaceSelectionChange}
                    requiredOptions={['external']}
                  />
                </section>
              </div>

              {/* Scanning Frequency + Subscription Tier Row */}
              <div className="grid grid-cols-1 gap-3">
                {/* Scanning Frequency Section */}
                <ScanningFrequencySelector
                  selectedFrequency={
                    localSubscription.scanSchedule as ScanFrequency
                  }
                  onFrequencyChange={frequency =>
                    handleSubscriptionChange({ scanSchedule: frequency })
                  }
                />

                {/* Subscription Tier Section */}
                <SubscriptionTierSelector
                  selectedTier={localSubscription.selectedTier}
                  onTierChange={tier => {
                    // Professional and Ultimate tiers include web attack surface
                    const isProfessionalOrUltimate =
                      tier === 'professional' || tier === 'ultimate';
                    const updatedAttackSurface = isProfessionalOrUltimate
                      ? {
                          ...localSubscription.attackSurface,
                          applications: true,
                        }
                      : {
                          ...localSubscription.attackSurface,
                          applications: false,
                        };

                    // Default application coverage to actual assessment count for Professional/Ultimate
                    // Only set if current value is 0 (don't override saved values)
                    // Preserve values when switching to lower tiers — calculator ignores them,
                    // but they're restored if user switches back (values only reset on Save/Cancel)
                    const updatedNumberOfApplications =
                      isProfessionalOrUltimate &&
                      localSubscription.numberOfApplications === 0
                        ? actualTotalApplications
                        : localSubscription.numberOfApplications;

                    handleSubscriptionChange({
                      selectedTier: tier,
                      attackSurface: updatedAttackSurface,
                      numberOfApplications: updatedNumberOfApplications,
                    });
                  }}
                  tiers={[
                    'freemium',
                    'foundational',
                    'professional',
                    'ultimate',
                  ]}
                />
              </div>

              {/* Pricing Sections - Praetorian users see Usage + MSRP (no negotiated price here) */}
              {isPraetorianUser && (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {/* Usage Price Section */}
                  <UsagePriceSection
                    actualTotalAssets={displayTotalAssets}
                    actualTotalApplications={
                      tierIncludesWebApps ? actualTotalApplications : 0
                    }
                    actualTotalCodeRepos={actualTotalCodeRepos}
                    usagePrice={usagePrice}
                    usageFloor={usageFloor}
                  />

                  {/* MSRP Price Section */}
                  <MSRPPriceSection
                    localSubscription={localSubscription}
                    onAssetChange={value =>
                      setLocalSubscription(prev => ({
                        ...prev,
                        numberOfAssets: value,
                      }))
                    }
                    onAppChange={value =>
                      setLocalSubscription(prev => ({
                        ...prev,
                        numberOfApplications: value,
                      }))
                    }
                    onAEVExerciseChange={value =>
                      setLocalSubscription(prev => ({
                        ...prev,
                        numberOfAEVExercises: value,
                      }))
                    }
                    onLLMChange={value =>
                      setLocalSubscription(prev => ({
                        ...prev,
                        numberOfLLMs: value,
                      }))
                    }
                    msrp={subscriptionMsrp}
                    floor={subscriptionFloor}
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 2: AI Credits */}
          {currentStep === 2 && (
            <div className="absolute inset-0 overflow-y-auto">
              <AICreditsSection
                selectedPack={
                  (localSubscription.aiCreditPack ?? 'none') as AICreditPackId
                }
                onPackChange={pack =>
                  setLocalSubscription(prev => ({
                    ...prev,
                    aiCreditPack: pack,
                  }))
                }
              />
            </div>
          )}

          {/* Step 3: Review & Save */}
          {currentStep === 3 && (
            <div className="absolute inset-0 space-y-4 overflow-y-auto">
              <ReviewSection
                localSubscription={localSubscription}
                tierIncludesWebApps={tierIncludesWebApps}
                isPraetorianUser={isPraetorianUser}
                totalMsrp={totalMsrp}
                subscriptionFloor={subscriptionFloor}
                aiCreditAnnualCost={aiCreditAnnualCost}
                aiCreditPackLabel={aiCreditPackData?.label || 'BYOAI'}
                aiCreditPackCreditsPerMonth={
                  aiCreditPackData?.creditsPerMonth || 0
                }
                subscriptionMsrp={subscriptionMsrp}
                usagePrice={usagePrice}
                onEstimatedCostChange={value =>
                  handleSubscriptionChange({ estimatedCost: value })
                }
              />
            </div>
          )}
        </div>

        {/* Custom footer */}
        <div className="border-chariot-stroke mt-4 flex items-center justify-between border-t pt-4">
          <button
            onClick={onClose}
            className="text-chariot-text-secondary hover:text-chariot-text-primary py-2 text-base font-medium transition-colors"
            data-testid="modal-cancel-button"
          >
            Cancel
          </button>
          <div className="flex items-center gap-2">
            {currentStep > 1 && (
              <Button
                onClick={() =>
                  setCurrentStep(prev => Math.max(prev - 1, 1) as 1 | 2 | 3)
                }
                variant="ghost"
                className="bg-chariot-background-primary px-4"
                data-testid="modal-back-button"
              >
                Back
              </Button>
            )}
            <Button
              onClick={
                currentStep === 3
                  ? handleSave
                  : () =>
                      setCurrentStep(prev => Math.min(prev + 1, 3) as 1 | 2 | 3)
              }
              className="ml-2 min-w-24 px-4"
              disabled={updateSubscriptionStatus === 'pending'}
              data-testid="modal-primary-button"
            >
              {currentStep === 3 && updateSubscriptionStatus === 'pending'
                ? 'Saving...'
                : currentStep === 3
                  ? 'Save'
                  : 'Next'}
            </Button>
          </div>
        </div>
      </Loader>
    </Modal>
  );
}
